import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { Button, Form, Input, SelectSearch } from "@telluscompany/design-system";
import { Filter } from "lucide-react";

import { useLocalization } from "@i18n";
import { cleanEmptyStrings } from "@shared/hooks";

import type {
  AdminStructureRaisingHistoryFilters,
  DamRaisingMethod,
} from "../../api/structures/structures.types";

export type AdminStructureRaisingHistoryFilterData = {
  stage: string;
  method?: DamRaisingMethod;
};

const DAM_RAISING_METHODS: DamRaisingMethod[] = [
  "downstream",
  "upstream",
  "centerLine",
];

const EMPTY_FORM: AdminStructureRaisingHistoryFilterData = {
  stage: "",
  method: undefined,
};

export type AdminStructureRaisingHistoryFilterProps = {
  onFilter: (filters: AdminStructureRaisingHistoryFilters) => void;
  onClear: () => void;
  initialFilters?: Partial<AdminStructureRaisingHistoryFilterData>;
};

export const AdminStructureRaisingHistoryFilter = ({
  onFilter,
  onClear,
  initialFilters,
}: AdminStructureRaisingHistoryFilterProps) => {
  const { translate } = useLocalization();

  const form = useForm<AdminStructureRaisingHistoryFilterData>({
    defaultValues: {
      stage: initialFilters?.stage ?? "",
      method: initialFilters?.method,
    },
  });

  const methodOptions = useMemo(
    () =>
      DAM_RAISING_METHODS.map((value) => ({
        value,
        label: translate(`admin.structures.raisingHistory.method.${value}`),
      })),
    [translate],
  );

  const handleFilter = (data: AdminStructureRaisingHistoryFilterData) => {
    onFilter(cleanEmptyStrings(data) as AdminStructureRaisingHistoryFilters);
  };

  const handleClear = () => {
    form.reset(EMPTY_FORM);
    onClear();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFilter)}>
        <div className="flex flex-column gap-5">
          <Input
            control={form.control}
            label={`${translate("admin.structures.raisingHistory.filter.label.stage")}:`}
            name="stage"
            type="text"
            data-testid="raising-history-filter-stage"
            placeholder={translate(
              "admin.structures.raisingHistory.filter.placeholder.stage",
            )}
          />
          <SelectSearch
            control={form.control}
            label={`${translate("admin.structures.raisingHistory.filter.label.method")}:`}
            name="method"
            placeholder={translate(
              "admin.structures.raisingHistory.filter.placeholder.method",
            )}
            options={methodOptions}
            clearable
          />
        </div>
        <div className="flex justify-end mb-4 gap-4">
          <Button type="button" variant="secondary" onClick={handleClear}>
            {translate(
              "admin.structures.raisingHistory.filter.button.clearFilters",
            )}
          </Button>
          <Button
            type="submit"
            data-testid="raising-history-filter-submit"
            iconLeft={<Filter fill="currentColor" />}
          >
            {translate("admin.structures.raisingHistory.table.filterButtonText")}
          </Button>
        </div>
      </form>
    </Form>
  );
};
